import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw, Image, MessageSquare, Star, Plus, Trash2, User } from "lucide-react";

const ManageTestimonialSection = () => {
  const [formData, setFormData] = useState({
    isActive: true,
    backgroundImage: "",
    subtitle: "",
    mainTitle: "",
    testimonials: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allyoutube01-2d46d9aff565.herokuapp.com/api/testimonial-section");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Input Change for simple fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle Testimonial Array Changes
  const handleTestimonialChange = (index, field, value) => {
    const updatedTestimonials = [...formData.testimonials];
    updatedTestimonials[index][field] = value;
    setFormData((prev) => ({ ...prev, testimonials: updatedTestimonials }));
  };

  const addTestimonial = () => {
    setFormData((prev) => ({
      ...prev,
      testimonials: [
        ...prev.testimonials,
        { 
          id: `t${Date.now()}`, 
          name: "New Name", 
          role: "Role", 
          image: "", 
          logo: "", 
          content: "Testimonial content goes here...", 
          rating: 5 
        },
      ],
    }));
  };

  const removeTestimonial = (index) => {
    setFormData((prev) => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index),
    }));
  };

  // Submit Data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allyoutube01-2d46d9aff565.herokuapp.com/api/testimonial-section", formData);
      setMessage({ text: "Testimonial section updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update section.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // Reset Data
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://allyoutube01-2d46d9aff565.herokuapp.com/api/testimonial-section/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><MessageSquare className="me-2" size={28} /> Manage Testimonials</h2>
        <button className="btn btn-outline-secondary" onClick={handleReset}>
          <RefreshCw size={18} className="me-1" /> Reset Defaults
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        
        {/* General Settings */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header text-dark fw-bold"><Image className="me-2" size={20} /> Section Settings</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark text-dark">Background Image URL</label>
                  <input
                    type="text" className="form-control" name="backgroundImage"
                    value={formData.backgroundImage} onChange={handleChange}
                    placeholder="assets/images/testimonial/bg.png"
                  />
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-dark">Subtitle</label>
                    <input type="text" className="form-control" name="subtitle" value={formData.subtitle} onChange={handleChange} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-dark">Main Title</label>
                    <input type="text" className="form-control" name="mainTitle" value={formData.mainTitle} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials List */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center fw-bold">
                <span><Star className="me-2" size={20} /> Testimonials</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addTestimonial}>
                  <Plus size={16} className="me-1"/> Add Testimonial
                </button>
              </div>
              <div className="card-body">
                {formData.testimonials.map((item, index) => (
                  <div key={item.id || index} className="card bg-light mb-3 p-3 border-0">
                    <div className="d-flex justify-content-between mb-3">
                      <strong><User size={18} className="me-2"/> Testimonial #{index + 1}</strong>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTestimonial(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="small text-muted">Person Name</label>
                        <input type="text" className="form-control" value={item.name} onChange={(e) => handleTestimonialChange(index, "name", e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted">Role / Designation</label>
                        <input type="text" className="form-control" value={item.role} onChange={(e) => handleTestimonialChange(index, "role", e.target.value)} />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted">Rating (1-5)</label>
                        <select className="form-select" value={item.rating} onChange={(e) => handleTestimonialChange(index, "rating", parseInt(e.target.value))}>
                          {[1, 2, 3, 4, 5].map(star => <option key={star} value={star}>{star} Stars</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted">Profile Image URL</label>
                        <input type="text" className="form-control" value={item.image} onChange={(e) => handleTestimonialChange(index, "image", e.target.value)} placeholder="assets/..." />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted">Client Logo URL</label>
                        <input type="text" className="form-control" value={item.logo} onChange={(e) => handleTestimonialChange(index, "logo", e.target.value)} placeholder="assets/..." />
                      </div>
                      <div className="col-12">
                        <label className="small text-muted">Testimonial Content</label>
                        <textarea className="form-control" rows="2" value={item.content} onChange={(e) => handleTestimonialChange(index, "content", e.target.value)}></textarea>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.testimonials.length === 0 && <p className="text-muted text-center">No testimonials added.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageTestimonialSection;